import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {isEqual} from 'lodash';

import {IState} from '../../../store/reducers';
import {CONTENT_TYPE} from '../../../../features/reports/constants';
import {ACTION_TYPES} from "../Tableau";
import {extractBlockName} from "../../../../features/reports/utils";
import {IBlock} from "../../../models";

const CONTENT_BLOCKS = [CONTENT_TYPE.TABLEAU, CONTENT_TYPE.EXTERNAL_FILE];

interface MergePdfOption {
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergePdfConfig {
  enabled: boolean;
  filename: string;
  options: MergePdfOption[];
}

export function useGetPdfMergeContent(
  initialConfig: MergePdfConfig,
  attachments: string[],
  onConfigChange: (event: any) => void): any {
  const [pdfMergeContent, setpdfMergeContent] = useState<IBlock[]>([]);
  const [mergePdfConfig, setMergePdfConfig] = useState<MergePdfConfig>({
    enabled: initialConfig.enabled || false,
    filename: initialConfig.filename || "",
    options: initialConfig.options || [],
  });

  const prevPdfMergeConfigRef = useRef(mergePdfConfig);
  const hasLoaded = useRef(false);
  const blocks = useSelector((state: IState) => state.block.blocks);

  useEffect(() => {
    if (!blocks || !attachments) {
      return;
    }

    // filter out blocks that are not PDF, sort by id
    let mergePdfContent = blocks
      .filter((block: any) =>
        CONTENT_BLOCKS.includes(block.type) &&
        (
          (block.type === CONTENT_TYPE.TABLEAU &&
            [ACTION_TYPES.VIEW_PDF, ACTION_TYPES.WORKBOOK_PDF].includes(block.content_json?.action)) ||
          block.type === CONTENT_TYPE.EXTERNAL_FILE
        )
      )
      .sort((a: any, b: any) => a.id - b.id);

    const selectedAttachments = attachments.map((attachment: any) => extractBlockName(attachment));

    mergePdfContent = mergePdfContent.filter((block: IBlock) =>
      block.name && selectedAttachments.includes(block.name)
    );

    setpdfMergeContent(mergePdfContent);

    // check if all blocks are removed
    const isRemoved = hasLoaded.current && (mergePdfContent.length < 2 || attachments.length < 2);

    if (isRemoved) {
      resetMergePdfConfig();
    } else {
      updateMergePdfConfig(mergePdfContent);
    }

    // mark that data has been loaded
    if (mergePdfContent.length > 0) {
      hasLoaded.current = true;
    }
  }, [blocks, attachments]);

  // reset MergePdfConfig when all blocks are removed
  const resetMergePdfConfig = () => {
    const newConfig: MergePdfConfig = {enabled: false, options: [], filename: ""};
    updateStateIfChanged(newConfig);
    onConfigChange(newConfig);
  };

  // update merge pdf config when blocks are added or removed
  const updateMergePdfConfig = (mergePdfContent: IBlock[]) => {
    const validUids = mergePdfContent.map((item) => item.uid);

    // remove options that are not in mergePdfContent
    const updatedOptions = (mergePdfConfig.options || [])
      .filter((option: MergePdfOption) => option.uid && validUids.includes(option.uid));

    // add new blocks that are not in options
    mergePdfContent.forEach((content: IBlock) => {
      if (!updatedOptions.some((opt: MergePdfOption) => opt.uid === content.uid)) {
        updatedOptions.push({
          uid: content.uid,
          include: true,
          position: updatedOptions.length + 1,
        });
      }
    });

    const newConfig: MergePdfConfig = {
      enabled: mergePdfConfig.enabled,
      options: updatedOptions,
      filename: mergePdfConfig.filename,
    };
    updateStateIfChanged(newConfig);
  };

  // prevent unnecessary state updates and infinite loops
  const updateStateIfChanged = (newConfig: MergePdfConfig) => {
    if (!isEqual(newConfig, prevPdfMergeConfigRef.current)) {
      prevPdfMergeConfigRef.current = newConfig;
      setMergePdfConfig((prev) => ({ ...prev, ...newConfig }));
      onConfigChange(newConfig);
    }
  };

  return {
    content: pdfMergeContent,
    mergePdfConfig,
    setMergePdfConfig
  };
}
